import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";

const DURATION = 5000;

export default function StoryViewer({ creators, startIndex = 0, onClose }) {
  const [ci, setCi] = useState(startIndex);
  const [si, setSi] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const startRef = useRef(Date.now());

  const creator = creators[ci];
  const stories = creator.stories;

  const goForward = useCallback(() => {
    setProgress(0);
    if (si < stories.length - 1) setSi(si + 1);
    else if (ci < creators.length - 1) {
      setCi(ci + 1);
      setSi(0);
    } else onClose();
  }, [si, ci, stories.length, creators.length, onClose]);

  const goBackward = useCallback(() => {
    setProgress(0);
    if (si > 0) setSi(si - 1);
    else if (ci > 0) {
      const prev = ci - 1;
      setCi(prev);
      setSi(creators[prev].stories.length - 1);
    }
  }, [si, ci, creators]);

  const switchCreator = useCallback(
    (dir) => {
      setProgress(0);
      if (dir === "next") {
        if (ci < creators.length - 1) {
          setCi(ci + 1);
          setSi(0);
        } else onClose();
      } else if (ci > 0) {
        setCi(ci - 1);
        setSi(0);
      }
    },
    [ci, creators.length, onClose]
  );

  useEffect(() => {
    startRef.current = Date.now();
    setProgress(0);
    if (paused) return;
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.min(1, elapsed / DURATION);
      setProgress(p);
      if (p >= 1) {
        clearInterval(id);
        goForward();
      }
    }, 30);
    return () => clearInterval(id);
  }, [ci, si, paused, goForward]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goForward();
      if (e.key === "ArrowLeft") goBackward();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goForward, goBackward]);

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black" data-testid="story-viewer">
      <div className="relative h-full w-full max-w-[460px] overflow-hidden bg-black sm:h-[92vh] sm:rounded-2xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${ci}-${si}`}
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            <img src={stories[si].image} alt="" className="h-full w-full object-cover" draggable={false} />
          </motion.div>
        </AnimatePresence>

        {/* gradients */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/80 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-black/90 to-transparent" />

        {/* progress bars */}
        <div className="absolute inset-x-0 top-0 z-30 flex gap-1.5 px-3 pt-3" data-testid="story-progress">
          {stories.map((_, idx) => (
            <div key={idx} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full bg-white"
                style={{ width: idx < si ? "100%" : idx === si ? `${progress * 100}%` : "0%" }}
              />
            </div>
          ))}
        </div>

        {/* header */}
        <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 pt-6">
          <div className="flex items-center gap-3">
            <img src={creator.avatar} alt={creator.name} className="h-9 w-9 rounded-full object-cover ring-2 ring-white/60" />
            <div>
              <p className="text-sm font-medium text-white">{creator.name}</p>
              <p className="text-[11px] tracking-wide text-white/60">{creator.handle}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="story-close" className="text-white/80 transition-colors hover:text-white">
            <X size={24} strokeWidth={1.5} />
          </button>
        </div>

        {/* drag layer for switching creators + tap zones */}
        <motion.div
          className="absolute inset-0 z-20"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.15}
          onDragStart={() => setPaused(true)}
          onDragEnd={(e, info) => {
            setPaused(false);
            if (info.offset.x < -70) switchCreator("next");
            else if (info.offset.x > 70) switchCreator("prev");
          }}
        >
          <div className="flex h-full w-full">
            <button aria-label="previous" data-testid="story-prev" onClick={goBackward} className="h-full w-1/3 cursor-default" />
            <button aria-label="next" data-testid="story-next" onClick={goForward} className="h-full w-2/3 cursor-default" />
          </div>
        </motion.div>

        {/* caption + CTA */}
        <div className="absolute inset-x-0 bottom-0 z-30 px-6 pb-8">
          <p className="font-serif-lux text-2xl font-light leading-snug text-white" data-testid="story-caption">
            {stories[si].caption}
          </p>
          <button
            onClick={() => {
              onClose();
              navigate(`/creator/${creator.slug}`);
            }}
            data-testid="story-view-profile"
            className="group mt-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] text-white backdrop-blur-sm transition-colors hover:bg-white/15"
          >
            View Profile
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
